// Pure helpers shared by the Shot Details and Pass Details side panels — ported from
// renderers-shotmap.js (renderDivergingBarChart / renderObvChart and the two panels'
// identical Opposition / Date / Time rows). Private to that file in the legacy (not on
// window.EventMapKit), shared by both panels so they can't drift — same here.

import type { ProtoEvent } from "@catbento/shared";
import { formatShortDate, matchResultDisplay, periodLabel, sbAttrs, type EventContext } from "../engines/event-map-kit.js";

export interface DivergingRowInput {
  label: string;
  value: unknown;
  isTotal?: boolean;
  /** Default: signed 3dp ("+0.011"). */
  format?: (v: number) => string;
}

/** One painted row: a bar from the zero line (left/width in %), or an honest "—". */
export interface DivergingRow {
  label: string;
  isTotal: boolean;
  hasValue: boolean;
  /** '' | 'positive' | 'negative' (the bar + value colour). */
  cls: string;
  leftPct: number;
  widthPct: number;
  text: string;
}

/** Diverging bars centred on zero. No override = scale to this row set's largest |value|
 *  (so OBV "against", always much smaller, isn't flattened by a global scale); an override
 *  holds a known bound constant (xG's 0–1). */
export function divergingBarRows(rows: DivergingRowInput[], maxAbsOverride?: number): DivergingRow[] {
  const vals = rows.map((r) => r.value).filter((v): v is number => typeof v === "number");
  const maxAbs = maxAbsOverride || Math.max(0.01, ...vals.map(Math.abs));
  return rows.map((r) => {
    const isTotal = !!r.isTotal;
    const v = r.value;
    if (typeof v !== "number") {
      return { label: r.label, isTotal, hasValue: false, cls: "", leftPct: 0, widthPct: 0, text: "—" };
    }
    const fmt = r.format ?? ((n: number) => `${n >= 0 ? "+" : ""}${n.toFixed(3)}`);
    const halfPct = (Math.abs(v) / maxAbs) * 50;
    return {
      label: r.label,
      isTotal,
      hasValue: true,
      cls: v > 0 ? "positive" : v < 0 ? "negative" : "",
      leftPct: v < 0 ? 50 - halfPct : 50,
      widthPct: halfPct,
      text: fmt(v),
    };
  });
}

/** The OBV chart (For / Against / Total), auto-scaled. */
export function obvChartRows(event: ProtoEvent): DivergingRow[] {
  const raw = sbAttrs(event);
  return divergingBarRows([
    { label: "For", value: raw.obv_for_net },
    { label: "Against", value: raw.obv_against_net },
    { label: "Total", value: raw.obv_total_net, isTotal: true },
  ]);
}

/** "1st Half · 23:05" — the panels' Time row. */
export function eventTimeLabel(event: ProtoEvent): string {
  return `${periodLabel(event.period)} · ${event.minute}:${String(Math.floor(event.second)).padStart(2, "0")}`;
}

/** The panels' Opposition / Date / Result rows. */
export function eventMatchRows(
  ctx: EventContext,
  event: ProtoEvent,
): { opposition: string; date: string; resultText: string; resultClass: string } {
  const { opposition, text, className } = matchResultDisplay(ctx, event);
  return {
    opposition: opposition ? `${opposition.opponent} (${opposition.isHome ? "H" : "A"})` : "—",
    date: formatShortDate(event.match_date),
    resultText: text,
    resultClass: className,
  };
}
